import { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@builder/store/store";
import AdvSelectInput from "@builder/components/app/Settings/StoreData/AdvSelectInput";
import { useDebounce } from "@builder/components/app/Settings/StoreData/useDebounce";
import { useGetOrdersQuery } from "@builder/store/slices/dataApiSlices/ordersApiSlice";
import { Order } from "@builder/types/Order.type";
import { setStoreData } from "@builder/store/slices/storeDataSlice";

interface OrderProps {
    companyId: string;
    organizationId?: string;
    isOrg: boolean | null;
}
const Orders = ({ companyId, organizationId, isOrg }: OrderProps) => {
    const dispatch = useAppDispatch();
    const order = useAppSelector(state => state.storeData.data?.order);
    const [options, setOptions] = useState<Order[]>([]);
    const [search, setSearch] = useState<string>("");
    const [skip, setSkip] = useState<number>(0);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(false);
    const [debouncedSearch, setDebouncedSearch] = useState<string>("");
    const debouncedSearchCallback = useDebounce((searchValue: string) => {
        setDebouncedSearch(searchValue);
    }, 300);

    const { data: orders, isFetching } = useGetOrdersQuery({
        companyId,
        search: debouncedSearch,
        offset: skip,
        limit: 25,
        includeCustomer: true,
        ...(isOrg && { organizationId: organizationId })
    });

    useEffect(() => {
        if (orders) {
            setLoading(false);
            if (skip === 0) {
                setOptions(orders);
            } else {
                setOptions(prev => {
                    const newOrders = orders.filter(order => !prev.some(existing => existing._id === order._id));
                    return [...prev, ...newOrders];
                });
            }
            setHasMore(orders.length === 25);
        }
    }, [orders, skip, dispatch, order]);

    useEffect(() => {
        debouncedSearchCallback(search);
    }, [search, debouncedSearchCallback]);

    useEffect(() => {
        setSkip(0);
        setDebouncedSearch("");
    }, [companyId]);

    useEffect(() => {
        setSkip(0);
        setHasMore(true);
    }, [debouncedSearch]);

    const handleScroll = () => {
        if (hasMore && !loading && !isFetching) {
            setLoading(true);
            setSkip(prevSkip => prevSkip + 25);
        }
    };

    const handleSearchChange = (value: string) => {
        setSearch(value);
    };

    const handleOptionChange = (value: Order) => {
        dispatch(setStoreData({ order: value }));
    };

    return (
        <AdvSelectInput
            value={order}
            options={options}
            inputValue={search}
            label="Orders"
            placeholder={options.length === 0 ? "No orders available" : ""}
            disabled={options.length === 0}
            hasMore={hasMore}
            findLabel={option =>
                `${option.receiptId ? "#" + option.receiptId : "#" + option.externalId}
                ${(option.receiptId || option.externalId) && (option.customer?.firstName || option.customer?.lastName) ? " - " : ""}${
                    option.customer?.firstName ? option.customer.firstName + " " : ""
                }${option.customer?.lastName ? option.customer.lastName : ""}`
            }
            clickCallback={handleOptionChange}
            scrollCallback={handleScroll}
            searchCallback={handleSearchChange}
        />
    );
};

export default Orders;
